/** @prettier */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { Dropdown, Option as DropdownOption } from '@xanadu/ui-shared/components/widgets/Dropdown';
import { PRODUCTS, PRODUCT_LINKS } from '@xanadu/ui-shared/constants';
import { changeProduct as changeProductAct } from '@xanadu/ui-shared/modules/application/actions';
import navigateExternal from '@xanadu/ui-shared/utils/navigateExternal';

import { trackEvent } from '../../../utils/analytics';

import ProductIcon from './ProductIcon';
import styles from './SubProducts.module.scss';

const SubProducts = ({
  product,
  changeProduct,
  virtualsAllowed,
  casinoVisibilityRestricted,
  subProductsEnabled,
  externalProductsEnabled = [],
  history,
  widthSize,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(product);

  // subProductsEnabled is the ordered list of visible product ids (internal and external);
  // an id resolves to its externalProductsEnabled entry when present, otherwise to PRODUCT_LINKS.
  const mergedProducts = subProductsEnabled.reduce((update, id) => {
    const externalProduct = externalProductsEnabled.find((externalLink) => externalLink.id === id);

    if (externalProduct) {
      return [...update, externalProduct];
    }

    const productLink = PRODUCT_LINKS.find((link) => link.id === id);

    if (!productLink) {
      return update;
    }

    if (casinoVisibilityRestricted && [PRODUCTS.casino, PRODUCTS.liveCasino].includes(id)) {
      return update;
    }

    if (!virtualsAllowed && id === PRODUCTS.virtuals) {
      return update;
    }

    return [...update, productLink];
  }, []);

  const showDropdownSubProducts = widthSize < 1365 && mergedProducts.length > 4;

  const navigate =
    ({ url, product: productUpdate }) =>
    () => {
      if (productUpdate) {
        changeProduct(productUpdate);
      }

      history.push(url);
    };

  const navigateToProduct = (productLink) => () => {
    trackEvent('Product Selected', {
      product: productLink.id,
      external: Boolean(productLink.external),
    });

    if (productLink.external) {
      navigateExternal({ url: productLink.url });
      return;
    }

    navigate({ url: productLink.url, product: productLink.id })();
  };

  const getProductClass = ({ id }) =>
    classNames({
      [styles.product]: true,
      [styles[`product--${id}`]]: true,
      [styles['product--active']]: product === id,
    });

  const mainClass = classNames({
    [styles.main]: true,
    [styles['main--dropdown']]: showDropdownSubProducts,
    [styles['main--compact']]: !showDropdownSubProducts && mergedProducts.length <= 2,
    [styles['main--spaced']]: !showDropdownSubProducts && mergedProducts.length > 2,
  });

  return (
    <div className={mainClass}>
      {showDropdownSubProducts ? (
        <Fragment>
          <Dropdown
            theme="dark"
            layout="iconLabel"
            className={styles.optionSelector}
            items={mergedProducts}
            value={selectedProduct}
            selectLabel="Select"
            onSelect={(data) => {
              if (!data.external) {
                setSelectedProduct(data.id);
              }

              navigateToProduct(data)();
            }}
            useDesktopDropdown
          >
            {mergedProducts.map((mergedProduct) => (
              <DropdownOption
                key={`${mergedProduct.id}-${mergedProduct.external ? 'external' : 'internal'}`}
                value={mergedProduct}
              >
                <div className={getProductClass(mergedProduct)}>
                  <div className={styles.iconContainer}>
                    <ProductIcon product={mergedProduct.id} className={styles.icon} />
                    <span>{mergedProduct.name}</span>
                  </div>
                </div>
              </DropdownOption>
            ))}
          </Dropdown>
        </Fragment>
      ) : (
        mergedProducts.map((mergedProduct) => (
          <button
            key={`${mergedProduct.id}-${mergedProduct.external ? 'external' : 'internal'}`}
            className={getProductClass(mergedProduct)}
            onClick={navigateToProduct(mergedProduct)}
          >
            <div className={styles.iconContainer}>
              <ProductIcon product={mergedProduct.id} className={styles.icon} />
              <span>{mergedProduct.name}</span>
            </div>
          </button>
        ))
      )}
    </div>
  );
};

SubProducts.propTypes = {
  product: PropTypes.string.isRequired,
  subProductsEnabled: PropTypes.array.isRequired,
  externalProductsEnabled: PropTypes.array,
  changeProduct: PropTypes.func.isRequired,
  virtualsAllowed: PropTypes.bool.isRequired,
  widthSize: PropTypes.number.isRequired,
  casinoVisibilityRestricted: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};

const mapDispatchToProps = {
  changeProduct: changeProductAct,
};

function mapStateToProps(state) {
  return {
    product: state.getIn(['application', 'product']),
    virtualsAllowed: state.getIn(['user', 'virtualsAllowed']),
    casinoVisibilityRestricted: state.getIn(['vegas', 'visibilityRestricted'], true),
    widthSize: state.getIn(['application', 'widthSize']),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SubProducts));
